import React, { FC, useEffect } from 'react';
import { useParams } from 'react-router';

import apiRoutes from '../../../../config/api/routes';
import { getPageMapper } from '../../../../config/api/models/Page';
import { Message, Loading } from '../../../shared/components';
import { useFetch } from '../../../shared/hooks';
import Page from './Page';
import { ExerciseSeriesType } from '../../../../config/api/models/Exercise';
import {
  TrajectorySectionOrWorksheetType,
  WorksheetType,
} from '../../../../config/api/models/Trajectory';
import { usePagesByType } from '../../hooks';
import { UrlParams } from '../../../shared/types';

type Props = {
  type: ExerciseSeriesType;
  readOnly?: boolean;
};

const WorkSheet: FC<Props> = ({ type, readOnly = false }) => {
  const { trajectoryId, bookId } = useParams<UrlParams>();
  if (!trajectoryId) throw Error('No trajectoryId found!');
  if (!bookId) throw Error('No bookId found!');

  const worksheetPages = usePagesByType(`worksheet_${type}` as TrajectorySectionOrWorksheetType);

  const [addPageResponse, addPage, addPageReset] = useFetch(
    apiRoutes.bookTrajectoryPages(bookId, trajectoryId),
    {
      method: 'POST',
      mapper: getPageMapper,
    }
  );

  useEffect(() => {
    if (readOnly) return;
    if (!worksheetPages.success) return;
    if (worksheetPages.data && worksheetPages.data.length > 0) return;
    addPage({
      body: {
        type: `worksheet_${type}` as WorksheetType,
      },
    });
  }, [addPage, readOnly, worksheetPages.data, worksheetPages.success, type]);

  useEffect(() => {
    if (addPageResponse.success) {
      addPageReset();
      worksheetPages.refetch().then();
    }
  }, [addPageResponse.success, worksheetPages, addPageReset]);

  if (!!worksheetPages.data && worksheetPages.data.length > 1) {
    console.warn(
      `Trajectory ${trajectoryId} has multiple worksheets of type ${type}, it should only have 1.`
    );
  }

  return (
    <>
      {(worksheetPages.pending || addPageResponse.pending) && <Loading />}
      {worksheetPages.error && <Message type="error">{worksheetPages.error}</Message>}
      {addPageResponse.error && <Message type="error">{addPageResponse.reason}</Message>}
      {!!worksheetPages.data && worksheetPages.data.length > 0 && (
        <Page
          id={worksheetPages.data[0].id}
          type={`worksheet_${type}`}
          readOnly={readOnly}
          bookId={bookId}
        />
      )}
    </>
  );
};

export default WorkSheet;
